package com.wintermar.inspection.feature.presentation.inspection.answer

import androidx.lifecycle.viewModelScope
import com.wintermar.inspection.core.domain.FlowResult
import com.wintermar.inspection.core.domain.Result
import com.wintermar.inspection.core.ui.component.connectivity_monitor.util.ConnectivityMonitor
import com.wintermar.inspection.core.ui.component.connectivity_monitor.util.ConnectivityMonitorStatus
import com.wintermar.inspection.core.ui.component.datetime.util.DateTimeUtil
import com.wintermar.inspection.core.ui.component.image.util.FileChunkModel
import com.wintermar.inspection.core.ui.component.image.util.FileModel
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarController
import com.wintermar.inspection.core.ui.component.snackbar.util.SnackBarVariant
import com.wintermar.inspection.feature.domain.model.inspection.InspectionModel
import com.wintermar.inspection.feature.domain.service.ChunkUploadService
import com.wintermar.inspection.feature.domain.service.PendingUploadCallback
import com.wintermar.inspection.feature.domain.service.PendingUploadService
import com.wintermar.inspection.feature.domain.service.SubmitRetryService
import com.wintermar.inspection.feature.domain.usecase.answer.SubmitAnswerUseCase
import com.wintermar.inspection.feature.domain.usecase.answer.UpsertAnswerUseCase
import com.wintermar.inspection.feature.domain.usecase.auth.GetUserUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.CompleteInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.GetInspectionDetailUseCase
import com.wintermar.inspection.feature.domain.usecase.inspection.UpdateProgressInspectionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListTemplateOptionUseCase
import com.wintermar.inspection.feature.domain.usecase.reference.GetListVesselUseCase
import com.wintermar.inspection.feature.presentation.inspection.landing.retryPendingSubmitPlatform
import com.wintermar.inspection.util.Constant
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

class InspectionAnswerViewModel(
    private val inspectionId: String,
    private val connectivityMonitor: ConnectivityMonitor,
    private val updateProgressInspectionUseCase: UpdateProgressInspectionUseCase,
    private val completeInspectionUseCase: CompleteInspectionUseCase,
    private val submitRetryService: SubmitRetryService,
    getInspectionDetailUseCase: GetInspectionDetailUseCase,
    getListTemplateOptionUseCase: GetListTemplateOptionUseCase,
    getListVesselUseCase: GetListVesselUseCase,
    getUserUseCase: GetUserUseCase,
    upsertAnswerUseCase: UpsertAnswerUseCase,
    submitAnswerUseCase: SubmitAnswerUseCase,
    chunkUploadService: ChunkUploadService,
    pendingUploadService: PendingUploadService,
) : AnswerViewModel(
    inspectionId = inspectionId,
    getInspectionDetailUseCase = getInspectionDetailUseCase,
    getListTemplateOptionUseCase = getListTemplateOptionUseCase,
    getListVesselUseCase = getListVesselUseCase,
    getUserUseCase = getUserUseCase,
    upsertAnswerUseCase = upsertAnswerUseCase,
    chunkUploadService = chunkUploadService,
    pendingUploadService = pendingUploadService,
    submitAnswerUseCase = submitAnswerUseCase
) {
    private val _navigationEvent = Channel<InspectionAnswerEvent>()
    val navigationEvent = _navigationEvent.receiveAsFlow()

    override val state = InspectionAnswerState(
        inspectionId = inspectionId,
        onSaveAnswer = { answer, answerType, includeUploadFile ->
            processAnswer(answer, answerType, includeUploadFile)
        },
        onUpdateProgressInspection = { vesselName, totalAnswer, skipAutoClose ->
            processUpdate(vesselName, totalAnswer, skipAutoClose)
        },
        onCompleteInspection = {
            handleOnCompleteInspection()
        },
        onSearchQuestion = {
            goToSearchQuestion()
        },
        onReattachUploadCallback = {
            reattachUploadCallbackResult()
        }
    )

    init {
        connectivityMonitor.start()
        initAnswerData()
        statusNetwork()
        reattachUploadCallbackResult()
    }

    private fun processUpdate(
        vesselName: String, totalAnswer: Int, skipAutoClose: Boolean
    ) = viewModelScope.launch {
        processUpdateProgressPlatform(
            onUpdate = {
                updateInspectionProgress(vesselName, totalAnswer, skipAutoClose)
            },
            onGoToLanding = {
                if (!skipAutoClose) goToLanding(
                    vesselName,
                    totalAnswer,
                    lastUpdatedIso = DateTimeUtil.getCurrentDateIsoUtc0()
                )
            }
        )
    }

    private fun updateInspectionProgress(
        vesselName: String,
        totalAnswer: Int,
        skipAutoClose: Boolean
    ) =
        viewModelScope.launch {
            val lastUpdatedIso = DateTimeUtil.getCurrentDateIsoUtc0()
            when (val result =
                updateProgressInspectionUseCase(
                    inspectionId,
                    totalAnswer,
                    vesselName,
                    lastUpdatedIso
                )) {
                is Result.Success -> {
                    if (skipAutoClose) {
                        return@launch
                    }

                    goToLanding(
                        vesselName,
                        totalAnswer,
                        lastUpdatedIso
                    )
                }

                is Result.Error -> SnackBarController.show(
                    message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                    variant = SnackBarVariant.ERROR
                )
            }
        }

    private fun statusNetwork() {
        connectivityMonitor.status.onEach { status ->
            when (status) {
                ConnectivityMonitorStatus.CONNECTED -> {
                    state.connectivityMonitorStatus = status
                    retryPendingSubmit()
                }

                ConnectivityMonitorStatus.DISCONNECTED -> {
                    state.connectivityMonitorStatus = status
                }

                ConnectivityMonitorStatus.UNKNOWN -> {
                    state.connectivityMonitorStatus = status
                }
            }
        }.launchIn(viewModelScope)
    }

    private fun completeInspection() {
        completeInspectionUseCase(inspectionId).onEach { result ->
            when (result) {
                is FlowResult.Loading -> {
                    state.isLoadingCompleteInspection = result.showLoading
                }

                is FlowResult.Error -> {
                    SnackBarController.show(
                        message = result.error?.message ?: Constant.UNKNOWN_ERROR,
                        variant = SnackBarVariant.ERROR
                    )
                }

                is FlowResult.Success -> {
                    result.data?.let {
                        state.onSuccessCompleteInspection(
                            status = it.first,
                            lastUpdate = it.second
                        )
                    }
                }
            }
        }.launchIn(viewModelScope)
    }

    private fun retryPendingSubmit() = viewModelScope.launch {
        retryPendingSubmitPlatform(
            onRetry = {
                submitRetryService.trigger()
                pendingUpload()
            }
        )
    }

    private suspend fun goToLanding(
        vesselName: String,
        totalAnswer: Int,
        lastUpdatedIso: String
    ) {
        _navigationEvent.send(
            InspectionAnswerEvent.NavigateToLanding(
                InspectionModel(
                    id = inspectionId,
                    vesselName = vesselName,
                    totalAnswer = totalAnswer,
                    totalQuestion = state.template.numberOfQuestions,
                    lastUpdated = lastUpdatedIso
                )
            )
        )
    }

    private fun goToSearchQuestion() {
        viewModelScope.launch {
            _navigationEvent.send(InspectionAnswerEvent.NavigateToSearchQuestion(state.template))
        }
    }

    private fun pendingUpload() = viewModelScope.launch {
        val chunkCallback = buildChunkUploadCallback()
        pendingUploadService.retryPendingUploads(
            callback = object : PendingUploadCallback {
                override fun onStarted(files: List<FileModel>) {}
                override fun onProgress(questionId: String, chunk: FileChunkModel) =
                    chunkCallback.onProgress(questionId, chunk)

                override fun onFileCompleted(
                    questionId: String,
                    fileId: String,
                    chunk: FileChunkModel?
                ) =
                    chunkCallback.onCompleted(questionId, fileId, chunk)

                override fun onFileError(questionId: String, fileId: String, error: Throwable?) =
                    chunkCallback.onError(questionId, fileId, error)

                override fun onCompleted() {}
                override fun onError(error: Throwable?) {
                    viewModelScope.launch {
                        SnackBarController.show(
                            message = error?.message ?: Constant.UNKNOWN_ERROR,
                            variant = SnackBarVariant.ERROR
                        )
                    }
                }

                override fun onNothingPending() {}
                override fun onSkipped(reason: String) {}
            }
        )
    }

    private fun handleOnCompleteInspection() {
        if (!state.isAllFileUploaded()) {
            viewModelScope.launch {
                SnackBarController.show(
                    message = "File upload is not complete, please wait until upload is finished.",
                    variant = SnackBarVariant.WARNING
                )
            }
            return
        }
        completeInspection()
    }

    override fun onCleared() {
        super.onCleared()
        connectivityMonitor.stop()
    }
}

expect suspend fun processUpdateProgressPlatform(
    onUpdate: suspend () -> Unit,
    onGoToLanding: suspend () -> Unit
)
